import React, { useState, useMemo, useRef } from "react";
import { Search } from "lucide-react";
import { ScheduleItem } from "../types";
import { toLocalMidnight } from "../utils";
import PlanningCellDetailModal from "./PlanningCellDetailModal";

interface PlanningViewProps {
  data: ScheduleItem[];
}

const PlanningView: React.FC<PlanningViewProps> = ({ data }) => {
  const [windowDays, setWindowDays] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [hideFull, setHideFull] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ teacher: string, date: Date, items: ScheduleItem[] } | null>(null);
  const [isExportingImage, setIsExportingImage] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  
  const today = useMemo(() => toLocalMidnight(new Date()), []);
  const planningDates = useMemo(() => Array.from({ length: windowDays }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      return d;
    }), [today, windowDays]);

  const resourceMatrix = useMemo(() => {
    const groups: { [key: string]: ScheduleItem[] } = {};
    data.forEach(item => {
      if (!groups[item.teacher]) groups[item.teacher] = [];
      groups[item.teacher].push(item);
    });

    const rows = Object.keys(groups).map(teacher => {
      const items = groups[teacher];
      const dailyLoad = planningDates.map(date => {
        const time = date.getTime();
        const activeItems = items.filter(item => {
           const s = toLocalMidnight(new Date(item.startDate));
           const e = toLocalMidnight(new Date(item.endDate));
           return time >= s.getTime() && time <= e.getTime();
        });
        const startingItems = items.filter(item => {
           const s = toLocalMidnight(new Date(item.startDate));
           return time === s.getTime();
        });
        let pivotLabel: string | null = null;
        let isGoldenPivot = false;
        let runningClassType = '';

        if (activeItems.length === 1) {
           const active = activeItems[0];
           runningClassType = active.type;
           const s = toLocalMidnight(new Date(active.startDate));
           const timeUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
           const sUTC = Date.UTC(s.getFullYear(), s.getMonth(), s.getDate());
           const diffDays = Math.round((timeUTC - sUTC) / (86400000));
           
           if (active.type === '21Day') {
              if (diffDays >= 11 && diffDays <= 12) { pivotLabel = `D${diffDays - 3}`; isGoldenPivot = true; } 
              else if (diffDays >= 19 && diffDays <= 20) { pivotLabel = `D${diffDays - 3}`; isGoldenPivot = true; }
           } else {
              if (diffDays >= 13 && diffDays <= 14) { pivotLabel = `D${diffDays - 3}`; isGoldenPivot = true; }
           }
        }
        return { count: activeItems.length, hasNewIntake: startingItems.length > 0, items: activeItems, pivotLabel, isGoldenPivot, runningClassType };
      });
      const availabilityScore = dailyLoad.reduce((acc, curr) => {
         if (curr.count === 0) return acc + 3; 
         if (curr.count === 1 && curr.isGoldenPivot) return acc + 2; 
         if (curr.count === 1) return acc + 1; 
         return acc;
      }, 0);
      return { teacher, dailyLoad, availabilityScore };
    });
    return rows.sort((a, b) => b.availabilityScore - a.availabilityScore);
  }, [data, planningDates]);

  const filteredRows = useMemo(() => {
    let res = resourceMatrix;
    if (searchTerm.trim()) res = res.filter(r => r.teacher.toLowerCase().includes(searchTerm.toLowerCase()));
    if (hideFull) res = res.filter(r => r.availabilityScore > 0);
    return res;
  }, [resourceMatrix, searchTerm, hideFull]);

  // Export functions (kept simple for PlanningView)
  const handleExportExcel = () => { /* ... */ };
  const handleExportImage = () => { /* ... */ };
  const handleExportHTML = () => { /* ... */ };
  
  return (
    <div className="backdrop-blur-sm bg-white/90 rounded-2xl shadow-2xl border border-slate-200/60 flex flex-col h-[calc(100vh-140px)]">
       <div className="p-5 border-b border-slate-200/60 flex items-center justify-between bg-gradient-to-r from-slate-50 to-blue-50/40 rounded-t-2xl">
          <div className="flex items-center gap-4">
             <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-blue-500 transition-colors" size={16} />
                <input 
                  type="text" 
                  placeholder="搜索老师..." 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                  className="pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-72 bg-white/80 backdrop-blur-sm transition-all hover:bg-white hover:shadow-md" 
                />
             </div>
             <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-slate-300 shadow-sm">
                <span className="text-xs text-slate-600 font-medium">窗口天数:</span>
                <select 
                  value={windowDays} 
                  onChange={e => setWindowDays(Number(e.target.value))} 
                  className="border-0 bg-transparent text-sm font-bold text-slate-700 outline-none focus:ring-0 cursor-pointer"
                >
                  <option value={7}>7天</option>
                  <option value={10}>10天</option>
                  <option value={14}>14天</option>
                  <option value={21}>21天</option>
                </select>
             </div>
             <label className="flex items-center gap-2 text-sm text-slate-700 font-medium bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-slate-300 cursor-pointer hover:bg-white hover:shadow-md transition-all">
                <input 
                  type="checkbox" 
                  checked={hideFull} 
                  onChange={e => setHideFull(e.target.checked)} 
                  className="rounded border-slate-400 text-blue-600 focus:ring-2 focus:ring-blue-500" 
                />
                隐藏已满负荷
             </label>
          </div>
          <div className="flex items-center gap-4">
             {/* ExportMenu could be added here if needed */}
          </div>
       </div>
       <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50/30 to-blue-50/30 relative" ref={tableRef}>
           <table className="w-full border-collapse">
             <thead className="sticky top-0 bg-white/95 backdrop-blur-md z-10 shadow-md">
                <tr>
                   <th className="sticky left-0 z-20 bg-gradient-to-r from-slate-100 to-slate-50 border-r border-slate-300 p-3 text-left text-xs font-bold text-slate-700 shadow-lg">老师</th>
                   <th className="p-3 text-center text-xs font-bold text-slate-700 bg-white/95">可用分</th>
                   {planningDates.map((date, idx) => (
                      <th key={idx} className="p-2 text-center text-xs font-bold text-slate-600 border-l border-slate-100 bg-white/95">
                         <div>{['日','一','二','三','四','五','六'][date.getDay()]}</div>
                         <div className="text-[10px] font-mono text-slate-500">{date.getMonth()+1}.{date.getDate()}</div>
                      </th>
                   ))}
                </tr>
             </thead>
             <tbody className="divide-y divide-slate-100">
                {filteredRows.map((row) => (
                   <tr key={row.teacher} className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/30 transition-all duration-200 group">
                      <td className="sticky left-0 z-10 bg-white/95 backdrop-blur-sm group-hover:bg-gradient-to-r group-hover:from-blue-50/80 group-hover:to-indigo-50/50 border-r border-slate-200 p-3 font-bold text-slate-800 text-sm shadow-[2px_0_8px_rgba(0,0,0,0.04)] transition-all">{row.teacher}</td>
                      <td className="p-3 text-center bg-white/50 group-hover:bg-white/70 transition-colors">
                        <div className={`inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-bold shadow-sm transition-all group-hover:scale-110 ${row.availabilityScore >= 5 ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white' : (row.availabilityScore > 0 ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' : 'bg-slate-200 text-slate-500')}`}>{row.availabilityScore}</div>
                      </td>
                      {row.dailyLoad.map((cell, idx) => {
                         const date = planningDates[idx];
                         let content = null;
                         if (cell.count === 0) { 
                           content = (
                             <div className="w-full h-full flex items-center justify-center group">
                               <div className="w-2 h-2 rounded-full bg-gradient-to-br from-emerald-300 to-green-400 shadow-sm group-hover:scale-150 group-hover:shadow-md transition-all"></div>
                             </div>
                           ); 
                         } else if (cell.isGoldenPivot) { 
                           content = (
                             <div 
                               className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 hover:shadow-lg hover:scale-105 transition-all duration-200 p-1" 
                               onClick={() => setSelectedCell({ teacher: row.teacher, date, items: cell.items })}
                             >
                               <span className="text-[10px] font-bold text-blue-700 leading-none mb-0.5">{cell.pivotLabel}</span>
                               <span className="text-[9px] text-blue-500 scale-90">{cell.runningClassType === '21Day' ? '21天' : '15天'}</span>
                             </div>
                           ); 
                         } else { 
                           const isFull = cell.count > 1; 
                           content = (
                             <div 
                               className={`w-full h-full rounded-lg flex items-center justify-center font-bold text-xs cursor-pointer transition-all duration-200 ${isFull ? 'bg-gradient-to-br from-red-50 to-rose-100 text-red-700 border border-red-200 hover:from-red-100 hover:to-rose-200 hover:shadow-lg hover:scale-105' : 'bg-gradient-to-br from-amber-50 to-orange-50 text-amber-700 border border-amber-200 hover:from-amber-100 hover:to-orange-100 hover:shadow-lg hover:scale-105'}`} 
                               onClick={() => setSelectedCell({ teacher: row.teacher, date, items: cell.items })}
                             >
                               {cell.count}
                             </div>
                           ); 
                         }
                         return (<td key={idx} className="p-1 h-14 relative bg-white/50">{content}</td>);
                      })}
                   </tr>
                ))}
             </tbody>
           </table>
       </div>
       {selectedCell && <PlanningCellDetailModal data={selectedCell} onClose={() => setSelectedCell(null)} />}
    </div>
  );
};

export default PlanningView;
